import React, { useCallback, useEffect, useRef, useState } from "react";
import { SCREENSHOTS } from "../../data/mock";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Reveal from "./Reveal";

/**
 * Screenshots — unified horizontal carousel (desktop + mobile).
 *
 * One viewport tall. Copy on the left, phone on the right (stacks vertically
 * on narrow viewports). Navigation via:
 *   - Prev / next arrow buttons
 *   - Clickable dots
 *   - Horizontal swipe (touch) and mouse drag
 *   - Left / right arrow keys when focused
 *
 * Scenes cross-fade in/out — the copy stack and phone stack both stack on
 * top of each other and only the active scene is visible. Screenshots are
 * Approved store marketing compositions that already contain their own phone
 * frame + headline + decorations, so we don't wrap them in another mockup.
 */
const Screenshots = () => {
  const [index, setIndex] = useState(0);
  const total = SCREENSHOTS.length;

  const goTo = useCallback(
    (i) => setIndex(((i % total) + total) % total),
    [total]
  );
  const prev = useCallback(() => setIndex((i) => (i - 1 + total) % total), [total]);
  const next = useCallback(() => setIndex((i) => (i + 1) % total), [total]);

  // Keyboard: left/right while the carousel is in focus.
  const stageRef = useRef(null);
  useEffect(() => {
    const node = stageRef.current;
    if (!node) return;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") { e.preventDefault(); prev(); }
      else if (e.key === "ArrowRight") { e.preventDefault(); next(); }
    };
    node.addEventListener("keydown", onKey);
    return () => node.removeEventListener("keydown", onKey);
  }, [prev, next]);

  // Touch swipe + mouse drag. IMPORTANT: handlers live on the inner grid
  // (NOT the outer stage), so the prev/next arrow buttons are siblings —
  // they can receive their own click events without being eaten by
  // pointer capture.
  const dragStartX = useRef(null);
  const dragDeltaX = useRef(0);
  const [dragging, setDragging] = useState(false);

  const onPointerDown = (e) => {
    // Let interactive children (dots) handle their own events.
    if (e.target.closest(".dot, button, a, input")) return;
    dragStartX.current = e.clientX;
    dragDeltaX.current = 0;
    setDragging(true);
  };
  const onPointerMove = (e) => {
    if (dragStartX.current == null) return;
    dragDeltaX.current = e.clientX - dragStartX.current;
  };
  const onPointerUp = () => {
    if (dragStartX.current == null) return;
    const dx = dragDeltaX.current;
    dragStartX.current = null;
    dragDeltaX.current = 0;
    setDragging(false);
    if (Math.abs(dx) > 48) {
      if (dx < 0) next();
      else prev();
    }
  };

  const active = SCREENSHOTS[index];

  return (
    <section className="carousel-section" id="screenshots">
      <div className="container">
        <Reveal className="section-header">
          <span className="eyebrow-bright">See it in action</span>
          <h2 className="section-title" style={{ marginTop: 10 }}>
            A glimpse of your new{" "}
            <span className="gradient-text">training companion.</span>
          </h2>
        </Reveal>

        <div
          ref={stageRef}
          className={`carousel-stage ${dragging ? "is-dragging" : ""}`}
          tabIndex={0}
          role="region"
          aria-roledescription="carousel"
          aria-label="Product screenshots"
        >
          {/* Prev / next arrows — siblings of the drag target so pointer
              capture can't eat their clicks. */}
          <button
            type="button"
            className="carousel-arrow carousel-arrow-prev"
            onClick={prev}
            aria-label="Previous screenshot"
          >
            <ChevronLeft size={22} />
          </button>
          <button
            type="button"
            className="carousel-arrow carousel-arrow-next"
            onClick={next}
            aria-label="Next screenshot"
          >
            <ChevronRight size={22} />
          </button>

          <div
            className="carousel-grid"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          >
            {/* Copy (stacked / cross-fade) */}
            <div className="carousel-copy">
              {SCREENSHOTS.map((scene, i) => (
                <div
                  key={scene.id}
                  className="scene"
                  data-active={i === index}
                  aria-hidden={i !== index}
                >
                  <div className="scene-number">
                    {String(i + 1).padStart(2, "0")}
                    <span className="scene-total">
                      / {String(total).padStart(2, "0")}
                    </span>
                  </div>
                  <h3 className="scene-title">{scene.title}</h3>
                  <p className="scene-description">{scene.description}</p>
                </div>
              ))}
              {/* Progress + dots */}
              <div className="carousel-controls-row">
                <div className="scene-progress-track">
                  <div
                    className="scene-progress-bar"
                    style={{ transform: `scaleX(${(index + 1) / total})` }}
                    aria-hidden
                  />
                </div>
                <div className="carousel-dots">
                  {SCREENSHOTS.map((_, i) => (
                    <button
                      key={i}
                      className={`dot ${i === index ? "active" : ""}`}
                      onClick={() => goTo(i)}
                      aria-label={`Go to screenshot ${i + 1}`}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Phone (stacked / cross-fade) */}
            <div className="carousel-visual">
              <div className="carousel-phone">
                {SCREENSHOTS.map((scene, i) => (
                  <img
                    key={scene.id}
                    src={scene.src}
                    alt={scene.alt}
                    width="660"
                    height="1434"
                    className="carousel-shot"
                    data-active={i === index}
                    loading={i < 2 ? "eager" : "lazy"}
                    draggable={false}
                  />
                ))}
              </div>
              <div className="carousel-orb" aria-hidden></div>
            </div>
          </div>
        </div>

        <p className="carousel-hint" aria-hidden>
          Swipe, tap the arrows, or use ← → keys
        </p>
      </div>
    </section>
  );
};

export default Screenshots;
